import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { filter, finalize, map, take, timeout } from 'rxjs';
import { DashboardService } from '../../services/dashboard.service';
import { LayoutService } from '../../../../layout/layout.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { DisabledDirective } from '../../../../shared/directives/disabled.directive';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { WaitService } from '../../../../shared/services/wait.service';
import domtoimage from 'dom-to-image';
import { DashboardTemplateService } from '../../services/dashboard-template.service';
import { AlertService } from '../../../../shared/services/alert.service';
import { DashboardTemplatesSelectComponent } from './dashboard-templates-select/dashboard-templates-select.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DashboardDto } from '../../../../../api-main';
import { DashboardTemplateModalComponent } from './dashboard-template-modal/dashboard-template-modal.component';
import { PrivilegesService } from '../../../../shared/services/privileges.service';
import { LocaleSessionService } from '../../../../shared/services/localeSession.service';
import { downloadBlob } from '../../../reports/reports-list/downloadBlob';
import { DropdownComponent } from '../../../../shared/components/dropdown/dropdown.component';
import { b64toBlob } from '../../../reports/reports-list/b64toBlob';
import { jsPDF } from 'jspdf';
import { toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-dashboard-header',
  templateUrl: './dashboard-header.component.html',
  styleUrls: ['./dashboard-header.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatButtonModule,
    MatIconModule,
    DisabledDirective,
    AsyncPipe,
    TranslateModule,
    DashboardTemplatesSelectComponent,
    DropdownComponent,
  ],
})
export class DashboardHeaderComponent {
  private dashboardService = inject(DashboardService);
  private dashboardTemplateService = inject(DashboardTemplateService);
  private dialog = inject(MatDialog);
  private translate = inject(TranslateService);
  private alertService = inject(AlertService);
  private layoutService = inject(LayoutService);
  private waitService = inject(WaitService);
  private privilegesService = inject(PrivilegesService);
  private localeService = inject(LocaleSessionService);

  get editMode(): boolean {
    return this.dashboardService.editMode$.value;
  }

  get isDownloading(): boolean {
    return this.dashboardService.dashboardIsDownloading();
  }

  get allowEdit(): boolean {
    return this.privilegesService.hasEditPrivileges(this.layoutService.selectedItem);
  }

  toggleEditMode(value: boolean = null) {
    if (value != null) this.dashboardService.editMode$.next(value);
    else this.dashboardService.editMode$.next(!this.editMode);
  }

  get changesMade() {
    return this.dashboardService.dashboardItemChanged;
  }

  private pngImage: string;

  dropDownStyles = {
    margin: 0,
    'min-width': 'unset',
  };

  dashboardName$ = toObservable(this.layoutService.selectedItemSignal).pipe(
    map((selectedItem) => {
      return selectedItem?.item_name ?? '';
    }),
  );

  restore() {
    if (this.changesMade)
      this.dashboardService.restoreDashboard().subscribe(() => {
        this.dashboardService.editMode$.next(false);
      });
    else this.dashboardService.editMode$.next(false);
  }

  createPng(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.dashboardService.dashboardFullyLoaded$
        .pipe(
          timeout(5000),
          filter((isLoaded) => isLoaded),
          take(1),
        )
        .subscribe({
          next: () => {
            const container = document.querySelector('.dashboard-container') as HTMLElement;
            const content = document.querySelector('mat-card-content') as HTMLElement;

            const itemTop = Array.from(container.querySelectorAll('gridster-item'))
              .map((item) => (item ? item.getBoundingClientRect() : null))
              .map((o) => {
                if (!o) return null;
                return o.top + o.height;
              })
              .filter((o) => !!o)
              .sort((a, b) => a - b);

            const height = Math.max(...itemTop);
            container.style.height = height + 'px';

            resolve(
              domtoimage
                .toPng(content)
                .then((value: string) => {
                  this.pngImage = value;
                  container.style.height = '100%';
                })
                .catch((error) => {
                  console.error('Failed to generate dashboard', error);
                }),
            );
          },
          error: (err) => {
            console.log('err dashboard header ', err);
            this.alertService.error(err.message);
            reject(err);
          },
        });
    });
  }

  downloadPng() {
    this.dashboardService.dashboardIsDownloading.set(true);
    this.waitService.start();
    this.createPng().then(() => {
      const itemName = this.layoutService.selectedItem.item_name ?? 'dashboard';
      const blob = b64toBlob(this.pngImage.split(',')[1], 'image/png');
      downloadBlob(blob, itemName, 'png');
      this.alertService.info(this.translate.instant('DASHBOARD.SAVED_SUCCESSFUL'));
      this.dashboardService.dashboardIsDownloading.set(false);
      this.waitService.stop();
    });
  }

  downloadPdf() {
    this.dashboardService.dashboardIsDownloading.set(true);
    this.waitService.start();
    this.createPng().then(async () => {
      const itemName = this.layoutService.selectedItem.item_name ?? 'dashboard';
      const img = new Image();
      img.src = this.pngImage;
      await img.decode();
      // console.log('Size: ', img.width, img.height);
      const doc = new jsPDF({
        orientation: img.height > img.width ? 'portrait' : 'landscape',
        unit: 'px',
        format: [img.height, img.width],
      });
      // const docWidth = doc.internal.pageSize.getWidth();
      // const docHeight = doc.internal.pageSize.getHeight();
      doc.addImage(this.pngImage, 'png', 0, 0, img.width, img.height);
      doc.save(itemName);
      this.alertService.info(this.translate.instant('DASHBOARD.SAVED_SUCCESSFUL'));
      this.dashboardService.dashboardIsDownloading.set(false);
      this.waitService.stop();
    });
  }

  downloadCsv() {
    this.dashboardService.dashboardIsDownloading.set(true);
    this.waitService.start();
    this.dashboardService
      .directDownloadDashboard(
        this.dashboardService.dashboard.id,
        this.localeService.lang,
        this.localeService.timezone,
        'csv',
      )
      .pipe(
        finalize(() => {
          this.dashboardService.dashboardIsDownloading.set(false);
          this.waitService.stop();
          this.alertService.info(this.translate.instant('DASHBOARD.SAVED_SUCCESSFUL'));
        }),
      )
      .subscribe({
        next: (res) => {
          const itemName = this.layoutService.selectedItem.item_name ?? 'dashboard';
          const file = JSON.parse(atob(res.file)).csvBuffer.data;
          const str = new TextDecoder().decode(Uint8Array.from(file));
          const blobCSV = new Blob([str], { type: 'text/csv' });
          downloadBlob(blobCSV, itemName, 'csv');
        },
        error: (err) => {
          console.error('Error: ', err);
        },
      });
  }

  showTemplateSelect() {
    return !this.showSaveAsTemplate() && !this.dashboardService.editMode$.value;
  }

  private hasDashboardItems(): boolean {
    return this.dashboardService.items?.length > 0;
  }

  showDownload() {
    return this.hasDashboardItems();
  }

  showSaveAsTemplate() {
    return this.hasDashboardItems();
  }

  private saveAsTemplate(dashboard: DashboardDto = null) {
    this.waitService.start();
    this.dashboardTemplateService
      .save(dashboard)
      .pipe(finalize(() => this.waitService.stop()))
      .subscribe({
        next: () => this.alertService.info(this.translate.instant('DASHBOARD.TEMPLATE_SAVED')),
        error: () => this.alertService.error(this.translate.instant('TEMPLATE.SELECT_ERROR')),
      });
  }

  openSaveTemplateModal() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      ...this.dashboardService.dashboard,
      name: this.layoutService.selectedItem.item_name,
    } as DashboardDto;
    const dialogRef = this.dialog.open(DashboardTemplateModalComponent, dialogConfig);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((data: DashboardDto) => {
        if (!data) return;
        // console.log('-> data', data);
        this.saveAsTemplate(data);
      });
  }
}
